/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import Open5eDocument from "./model/Open5eDocument";
import MultiSelectPopup from './MultiSelectPopup';

interface MultiSelectStringProps<T extends string | Open5eDocument> {
  values: string;
  valuesArray?: T[];
  options: T[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  singleSelection?: boolean;
  allowCustom?: boolean;
  showClear?: boolean;
  onSave?: (selectedValues: string) => void;
  onSaveArray?: (selectedValuesArray: T[]) => void;
  onClickOutside: (event: MouseEvent) => void;
  useMarkdown?: boolean;
  columns?: number;
  showGrouping?: boolean;
  className?: string
  maxSelections?: number;
  noWrap?: boolean;
  displayFunction?: (option: T) => string | JSX.Element;
  getIdFunction?: (option: T) => string;
  filterFunction?: (option: T) => boolean;
}

const MultiSelectString = <T extends string | Open5eDocument>({
  values,
  valuesArray,
  options,
  isOpen,
  setIsOpen,
  singleSelection = false,
  allowCustom = false,
  showClear = false,
  onSave,
  onSaveArray,
  onClickOutside,
  useMarkdown = false,
  columns = 2,
  showGrouping = true,
  className = '',
  maxSelections,
  noWrap = false,
  displayFunction,
  getIdFunction,
  filterFunction,
}: MultiSelectStringProps<T>) => {

  const separatorFunc = (o: T, index: number): string => {
    if (index === 0)
      return ''
    const val = displayFunction ? displayFunction(o) : o;
    if ((typeof val === 'string') && (val.includes(','))) {
      return '; ';
    }
    return  ', ';
  }

  const handleChange = (selectedValuesArray: T[]) => {
    const result: string = selectedValuesArray.map((o, index) => {
      const separator = separatorFunc(o, index);
      if (typeof o === 'object' && 'slug' in (o as any) && (o as any).slug === 'string') {
        return separator + (o as any).slug;
      }
      if (displayFunction) {
        const val = displayFunction(o);
        if (typeof val === 'string')
          return separator + val;
      }
      return separator + o;
    }).join('');

    if (onSaveArray) {
      onSaveArray(selectedValuesArray);
    }
    if (onSave) {
      onSave(result);
    }
  };

  const valueTextRef = useRef<HTMLSpanElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    event.stopPropagation();
    if (valueTextRef.current && !valueTextRef.current.contains(event.target as Node)) {
      onClickOutside(event);
    }
  };

  let content: React.ReactNode = values;

  if (valuesArray && displayFunction) {
    const contentArray = valuesArray.map((o, index) => (
      <React.Fragment key={index}>
        {separatorFunc(o, index)}
        {displayFunction(o)}
      </React.Fragment>
    ));

    if (contentArray.length) {
      content = contentArray;
    } else {
      content = null;
    }
  } 

  if (!content || content === '') {
    content = '\u00A0 -- \u00A0'; /* spaces to give a click target */
  }

  if (useMarkdown) {
    content = <ReactMarkdown>{content as string}</ReactMarkdown>;
  }

  const buttonStyle = {
    fontSize: '12px',
    marginLeft: '10px',
    borderRadius: '3px',
    border: '1px solid black',
    backgroundColor: '#a7d3fd',
    margin: '1px',
    padding: '2px 5px',
    verticalAlign: 'middle',
    opacity: '0',
    cursor: 'auto',
  };
  // If showClear is on, then we ALWAYS leave a spot for this button,
  // but it gets hidden when there are no values:
  if (values) {
    buttonStyle.opacity = '1'; 
    buttonStyle.cursor = 'pointer'; 
  }
  const clearButton = showClear && <button style={buttonStyle} onClick={() => showClear && values && onSave && onSave('')}>clear</button>

  return ( <>
      <span className="multi-select-container edit-field">
        {isOpen && (
          <MultiSelectPopup
            values={values}
            valuesArray={valuesArray}
            options={options}
            columns={columns}
            className={className}
            showGrouping={showGrouping}
            singleSelection={singleSelection}
            allowCustom={allowCustom}
            maxSelections={maxSelections}
            noWrap={noWrap}
            onChange={handleChange}
            onClickOutside={handleClickOutside}
            displayFunction={displayFunction}
            getIdFunction={getIdFunction}
            filterFunction={filterFunction}
          />
        )}
        <span onMouseDown={() => setIsOpen(true)} ref={valueTextRef}>
          {content}
        </span>
      </span>
      {clearButton}
    </>
    );
};

export default MultiSelectString;
